<template>
  <div class="share flex-none">
    <div class="d-flex flex-wrap align-items-center justify-contents-between">
      <b-btn
        v-if="!noDealerQuotes"
        variant="primary"
        :pressed="sharingEmail && sharingType === specialCases.sharingType.dealer"
        @click.prevent="sharePopup('dealer')"
      >
        Request a Quote
      </b-btn>
      <b-btn
        variant="primary"
        @click.prevent="sharePopup('pdf')"
      >
        Download/Print Summary
      </b-btn>
    </div>
    <div class="d-flex align-items-center justify-contents-between">
      <slot />

      <b-btn
        variant="outline-primary"
        :pressed="sharingEmail && sharingType === specialCases.sharingType.friend"
        @click.prevent="sharePopup('friend')"
      >
        <i class="fa fa-envelope-o" />
      </b-btn>
      <b-btn
        variant="outline-primary"
        @click.prevent="sharePopup('facebook')"
      >
        <i class="fa fa-facebook" />
      </b-btn>
      <b-btn
        variant="outline-primary"
        @click.prevent="sharePopup('twitter')"
      >
        <i class="fa fa-twitter" />
      </b-btn>
      <b-btn
        variant="outline-primary"
        @click.prevent="sharePopup('pinterest')"
      >
        <i class="fa fa-pinterest" />
      </b-btn>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapState, mapMutations, mapGetters } from 'vuex';
import get from 'lodash.get';
import { setProductPixelDimensions } from '@/helpers/dimensions';

export default {
  name: 'share',
  computed: {
    ...mapState([
      'currentLayer',
      'renderProject',
      'designUrl',
      'sharingEmail',
      'sharingType',
      'specialCases',
      'noDealerQuotes',
    ]),
    ...mapGetters([
      'applyToLayers',
      'currentProject',
      'currentProduct',
    ]),
  },
  methods: {
    get,
    ...mapMutations([
      'setDesignUrl',
      'setSharingEmail',
      'setSharingType',
    ]),
    getProjectShareData() {
      return this.currentProject && { imageRelative: this.renderProject.renderSrc };
    },
    getShareProduct(product) {
      const imageUrl = new URL(product.renderedImage);
      return {
        modelNumber: product.modelNumber,
        renderedImage: `${window.Renoworks.Util.getBaseApiUrl()}${imageUrl.pathname}`,
      };
    },
    getShareUrl(type, targetUrl, imagePath) {
      const shareParams = {
        twitter: {
          url: 'http://www.twitter.com/intent/tweet',
          params: {
            url: targetUrl,
          },
        },
        facebook: {
          url: 'http://www.facebook.com/sharer.php',
          params: {
            url: targetUrl,
          },
        },
        pinterest: {
          url: 'http://pinterest.com/pin/create/button',
          params: {
            url: targetUrl,
            media: imagePath,
          },
        },
        pdf: {
          url: targetUrl,
        },
      };
      const shareOptions = shareParams[type];
      const qs = window.jQuery.param(shareOptions.params || {});
      return `${shareOptions.url}${qs ? `?${qs}` : ''}`;
    },
    async renderResizedProductImage(product, layer) {
      // calculate and store product dimensions (in pixels) for rendering
      // project ppf is based upon layer dimensions and masked areas
      setProductPixelDimensions(product, layer);
      product.pattern = product.layerPatterns[layer.getPatternKey()];
      return product.render(); // returns Promise resolves to renderedImage path
    },
    async createProductOrProjectPayload(layers) {
      const data = { doors: [], site: window.Renoworks.client };
      if (this.currentProject) {
        data.project = this.getProjectShareData();
      }

      data.doors = await Promise.all(layers.map(async (layer) => {
        const product = layer.product || this.currentProduct;
        const renderedImage = await this.renderResizedProductImage(product, layer);
        const imageUrl = new URL(renderedImage);
        const productJson = {
          summary: product.layerPatterns[layer.getPatternKey()],
          width: layer.physicalWidth,
          height: layer.physicalHeight,
          renderedImage: imageUrl.pathname,
          product: this.getShareProduct(product),
          layer: this.getShareLayer(layer),
        };
        return productJson;
      }));

      return data;
    },
    async getShareEndpointInfo() {
      const data = new URLSearchParams();
      const { doors } = await this.createProductOrProjectPayload(this.applyToLayers);
      data.append('site', window.Renoworks.client);
      data.append('doors', JSON.stringify(doors));
      const projectRenderURL = this.currentProject && this.currentProject.renderSrc;
      const projectRenderRelative = projectRenderURL
        && projectRenderURL.replace(/^https?:\/\/[^/]+(\/.*)$/, '$1');
      data.append('project', JSON.stringify({ imageRelative: projectRenderRelative }));
      return {
        url: `${window.Renoworks.Util.getBaseApiUrl()}/Share`,
        data,
      };
    },
    async share(type, win) {
      const { url, data } = await this.getShareEndpointInfo();
      const response = await axios.post(url, data);
      const { path, id: shareId } = response.data;
      const imagePath = `${window.Renoworks.Util.getBaseApiUrl()}${path}`;
      const [baseHref, query] = window.location.href.split('?');
      const qs = `id=${shareId}${query ? `&${query}` : ''}`;
      const targetUrl = `${baseHref.replace(/\/$/, '')}/design-report?${qs}`;
      this.setDesignUrl(targetUrl);

      if (!win) {
        this.setSharingType(type);
        this.setSharingEmail(true);
      } else {
        win.location.href = this.getShareUrl(type, targetUrl, imagePath);
      }
    },
    sharePopup(type) {
      // popup blockers don't allow async-inited new windows, so open a window immediately...
      const win = type !== this.specialCases.sharingType.dealer
        && type !== this.specialCases.sharingType.friend
        && window.open('?loading=true');
      this.share(type, win);
    },
    // There is a bug when loading a saved door that it doens't always remember/get
    // the steps that it took to be created. So it won't get the steps and thus shareLayer here.
    getShareLayer(layer) {
      const shareLayer = { steps: [] };

      // Filter out unnecessary information that won't be on the DR page
      shareLayer.steps = layer.steps
        .filter(step => step.selected || step.type)
        .filter(step => step.name !== this.specialCases.stepNames.savedDoors)
        .map(step => ({
          name: step.name,
          selected: step.selected || step.type,
          sections: step.sections && step.sections
            .filter(section => section.selected)
            .map(section => ({
              name: section.name,
              selected: section.selected,
              categories: section.categories
                .filter(category => category.selected)
                .map(category => ({
                  name: category.name,
                  selected: category.selected,
                  items: category.items
                    .filter(item => item.selected)
                    .map(item => ({
                      name: item.name,
                      selected: item.selected,
                      extraData: {
                        numberOfThermometers: item.thermometers,
                        thermalRating: {
                          name: item.quality,
                        },
                        rValue: {
                          name: item.rValue,
                        },
                        descriptions: {
                          doorThickness: item.thickness,
                          constructionType: item.constructionType,
                          doorType: item.doorType,
                          guage: item.guage,
                          insulationType: item.insulation,
                        },
                      },
                    })),
                }),
                ),
            })),
        }));

      return shareLayer;
    },
  },
};
</script>

<style lang="scss">
@import "~@/assets/styles/variables.chi.scss";
@import "~bootstrap/scss/functions";
@import "~bootstrap/scss/variables";
@import "~bootstrap/scss/mixins";

.share {
  padding-left: $spacer / 4;
  padding-right: $spacer / 4;
  overflow-x: auto;

  div:first-child {
    margin-bottom: $spacer / 4;

    > * {
      margin: $spacer / 4;
    }
  }

  .btn {
    flex: auto;
    margin-left: $spacer / 4;
    margin-right: $spacer / 4;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Share.vue